import { useState } from "react";
import { ArrowUpRight, Rocket, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Data Center Services",
  "AI Infrastructure Software",
  "Energy & Sustainability",
  "Workforce Technology",
  "Construction Tech",
  "Media & Community",
];

const inputCls =
  "bg-white border border-[#D2D5DA] focus:border-[#0F1117] focus:outline-none px-3 py-2.5 text-[14px] text-[#0F1117] transition-colors";

const Label = ({ children }) => (
  <span className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#71747B]">
    {children}
  </span>
);

export default function VentureApplyModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    venture_name: "",
    category: CATEGORIES[0],
    pitch: "",
    linkedin: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${API}/api/ventures/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setStatus("error");
        setError(
          typeof data.detail === "string"
            ? data.detail
            : "Something went wrong. Please check your details and try again."
        );
        return;
      }
      setStatus("success");
    } catch {
      setStatus("error");
      setError("Network error. Please check your connection and try again.");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="venture-apply-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="venture-modal-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
    >
      <button
        type="button"
        aria-label="Close modal"
        onClick={onClose}
        className="absolute inset-0 bg-[#0F1117]/60 backdrop-blur-sm"
      />

      <div className="relative w-full max-w-lg chrome-surface p-6 md:p-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
        <button
          type="button"
          data-testid="venture-modal-close"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center text-[#5F6168] hover:text-[#0F1117] transition-colors"
        >
          <X size={16} />
        </button>

        {status !== "success" ? (
          <>
            <div className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#5F6168] mb-3 flex items-center gap-2">
              <Rocket size={11} aria-hidden />
              DCYB Ventures
            </div>
            <h3
              id="venture-modal-title"
              className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#0F1117] leading-tight mb-2"
            >
              Apply To Build.
            </h3>
            <p className="text-[13px] text-[#5F6168] leading-relaxed mb-6">
              We build companies with operators from the network. Every
              application receives a reply — straight to the founder's desk.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1.5">
                  <Label>Full Name</Label>
                  <input
                    type="text"
                    required
                    data-testid="venture-name-input"
                    value={form.name}
                    onChange={set("name")}
                    className={inputCls}
                    placeholder="Jane Doe"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <Label>Email</Label>
                  <input
                    type="email"
                    required
                    data-testid="venture-email-input"
                    value={form.email}
                    onChange={set("email")}
                    className={inputCls}
                    placeholder="jane@company.com"
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1.5">
                <Label>Venture Name</Label>
                <input
                  type="text"
                  required
                  data-testid="venture-company-input"
                  value={form.venture_name}
                  onChange={set("venture_name")}
                  className={inputCls}
                  placeholder="Working name of what you're building"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <Label>Focus Area</Label>
                <select
                  data-testid="venture-category-select"
                  value={form.category}
                  onChange={set("category")}
                  className={inputCls}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <Label>The Pitch</Label>
                <textarea
                  required
                  rows={4}
                  maxLength={2000}
                  data-testid="venture-pitch-input"
                  value={form.pitch}
                  onChange={set("pitch")}
                  className={`${inputCls} resize-none`}
                  placeholder="What are you building, the gap you see, and why you're the operator to build it."
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <Label>
                  LinkedIn{" "}
                  <span className="text-[#B0B4BA] normal-case tracking-normal">
                    (optional)
                  </span>
                </Label>
                <input
                  type="url"
                  data-testid="venture-linkedin-input"
                  value={form.linkedin}
                  onChange={set("linkedin")}
                  className={inputCls}
                  placeholder="https://linkedin.com/in/you"
                />
              </label>

              {error && (
                <div
                  data-testid="venture-modal-error"
                  className="mt-1 text-[12px] text-[#B03A3A] font-mono-label tracking-wide"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="venture-modal-submit"
                className="chrome-btn-dark mt-3 inline-flex items-center justify-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-5 py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "loading" ? (
                  <>Submitting…</>
                ) : (
                  <>
                    Submit Application
                    <ArrowUpRight size={14} />
                  </>
                )}
              </button>
              <p className="mt-1 font-mono-label text-[9.5px] tracking-[0.14em] uppercase text-[#9498A0] text-center">
                Routes directly to luke@dcyb.ai
              </p>
            </form>
          </>
        ) : (
          <div data-testid="venture-modal-success" className="text-center py-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#0F1117] text-white flex items-center justify-center mb-4">
              <Rocket size={18} />
            </div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-[#0F1117] mb-2">
              Application In.
            </h3>
            <p className="text-[13px] text-[#5F6168] leading-relaxed mb-6">
              Your application is on the founder's desk. Every application
              receives a reply — keep an eye on your inbox.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="venture-modal-done"
              className="chrome-btn-dark inline-flex items-center justify-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-6 py-3"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
